import { FloatingPortal } from "@floating-ui/react";
import { ApplicationView } from "@mixitone/mvc";
import clsx from "clsx";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { prefersReducedMotion } from "../animation";
import useGlobalShortcut from "../hooks/useGlobalShortcut";
import CloseIcon from "@mixitone/icons/close.svg";

interface Props extends React.PropsWithChildren {
  onClose: () => void;
  title?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  portal?: boolean;
  retainHeight?: boolean;
  ref?: React.MutableRefObject<HTMLElement | undefined> | React.RefCallback<HTMLElement | null>;
}

const ControlledModal: React.FC<Props> = ({
  onClose,
  title,
  children,
  className,
  style,
  portal,
  retainHeight,
  ref,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [hasMoreContent, setHasMoreContent] = useState(false);
  const [maxHeight, setMaxHeight] = useState(0);

  const checkScrollable = useCallback(() => {
    if (contentRef.current) {
      const { scrollHeight, clientHeight, scrollTop } = contentRef.current;
      // Check if content is scrollable and not at the bottom
      const isScrollable = scrollHeight > clientHeight;
      const isNotAtBottom = scrollTop < scrollHeight - clientHeight - 10; // 10px threshold
      setHasMoreContent(isScrollable && isNotAtBottom);
    }
  }, []);

  useLayoutEffect(() => {
    if (retainHeight && contentRef.current) {
      setMaxHeight((h) => Math.max(h, contentRef.current!.scrollHeight));
    }
  }, [children, retainHeight]);

  // Check on initial render and when content changes
  useEffect(() => {
    checkScrollable();

    // Use ResizeObserver to detect content size changes
    const resizeObserver = new ResizeObserver((entries) => {
      checkScrollable();
      if (retainHeight) {
        for (const entry of entries) {
          setMaxHeight((h) => Math.max(h, (entry.target as HTMLElement).scrollHeight));
        }
      }
    });

    // Use MutationObserver to detect DOM changes within the modal content
    const mutationObserver = new MutationObserver(() => {
      // Use setTimeout to ensure we check after the DOM has fully updated
      setTimeout(checkScrollable, 0);
    });

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);

      // Observe all changes to the DOM tree inside the modal content
      mutationObserver.observe(contentRef.current, {
        childList: true, // Observe direct children changes
        subtree: true, // Observe all descendants
        attributes: true, // Observe attribute changes that might affect layout
        characterData: true, // Observe text content changes
      });
    }

    return () => {
      if (contentRef.current) {
        resizeObserver.unobserve(contentRef.current);
      }
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [checkScrollable, children, retainHeight]);

  // Add scroll event listener
  useEffect(() => {
    const content = contentRef.current;
    if (content) {
      content.addEventListener("scroll", checkScrollable);
      return () => {
        content.removeEventListener("scroll", checkScrollable);
      };
    }
  }, [checkScrollable]);

  const handleClose = useCallback(async () => {
    if (modalRef.current && !prefersReducedMotion.matches) {
      // await animateFadeTo(modalRef.current, 1.0, 0, { duration: 100 });
    }

    if (onClose) {
      return onClose();
    }
  }, [onClose]);

  useGlobalShortcut("Escape", handleClose);

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const modalStyle = { ...style };
  if (retainHeight && maxHeight > 0) {
    modalStyle.minHeight = `${maxHeight}px`;
  }

  const modal = (
    // Background div
    <div
      ref={modalRef}
      className={clsx(
        "max-w-screen fixed inset-0 flex max-h-screen items-center justify-center bg-slate-700/50 backdrop-blur-sm transition-opacity",
        { "z-50": portal },
      )}
      onClick={handleBackgroundClick}
    >
      {/* Wrapper that includes outside close button if the modal has no title */}
      <div className="relative h-full sm:h-auto sm:max-h-[calc(100vh-4rem)] flex w-full sm:w-auto">
        {!title && (
          <button
            onClick={handleClose}
            title="Close"
            className="cursor-pointer absolute right-0 sm:-right-3 sm:-top-3 z-10"
          >
            <CloseIcon className="w-8 fill-black stroke-white transition-transform hover:scale-125" />
          </button>
        )}
        {/* Modal window */}
        <div
          className={twMerge(
            "flex h-full max-h-full w-full min-w-0 max-w-[100vw] flex-col border border-gray-300 bg-white px-2 pb-6 shadow-xl ring-1 ring-black/5 sm:mx-6 sm:h-auto sm:w-auto sm:min-w-[400px] sm:rounded-lg sm:px-6 relative overflow-y-auto overflow-x-hidden",
            className,
          )}
          ref={(el) => {
            contentRef.current = el;
            if (typeof ref === "function") {
              ref(el);
            } else if (ref && "current" in ref) {
              ref.current = el ?? undefined;
            }
          }}
          role="dialog"
          style={modalStyle}
          data-testid={`modal-${title}`}
        >
          {title && (
            <div className="-mx-2 mb-2 medium:mb-4 flex items-center justify-between bg-slate-800 px-4 py-2 text-white sm:-mx-6 sm:rounded-t-lg">
              <h2 className="medium:text-xl font-normal">{title}</h2>
              <button onClick={handleClose} title="Close" className="cursor-pointer">
                <CloseIcon className="w-[30px] fill-white transition-transform hover:scale-125 sm:w-[19px]" />
              </button>
            </div>
          )}
          {children}

          {/* Scroll indicator */}
          {hasMoreContent && (
            <div
              className="absolute bottom-0 left-0 right-0 h-12 pointer-events-none"
              style={{
                background:
                  "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,0.8) 50%, rgba(255,255,255,1) 100%)",
                boxShadow: "0 -4px 6px -1px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div className="flex justify-center items-end h-full pb-1">
                <svg
                  className="w-6 h-6 text-gray-500 animate-bounce"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (portal) {
    return <FloatingPortal>{modal}</FloatingPortal>;
  }

  return modal;
};

const Modal = ApplicationView(ControlledModal);
export { Modal };

export const ModalFooter: React.FC<React.PropsWithChildren> = ({ children }) => {
  return (
    <div className="flex justify-between border-t border-gray-200 bg-gray-50 px-5 py-3 tall:py-5 -mx-6 -mb-6 rounded-b-lg">
      {children}
    </div>
  );
};
